import { Outlet } from 'react-router-dom'
import styled from 'styled-components'
import { core } from '@musinsa/pbo-core'
import MainFooter from '@/components/layout/MainFooter'
import MainHeader from '@/components/layout/MainHeader'
import MainSideBar from '@/components/layout/MainSideBar'
import MainMenuOnSession from '@/components/layout/MainMenuOnSession'
import MenuGuard from '@/components/layout/MenuGuard'
import { FOOTER_HEIGHT, HEADER_HEIGHT, LAYOUT_CONTENT_PADDING } from '@/utils/consts'
import { Utils } from '@/utils/Utils'
import { MenuOnSessionProvider } from '@/components/layout/MainMenuOnSessionProvider'
import { useProgressModal } from '@/contexts/ProgressModalContext'

/**
 * pbo-core(antd) 화면의 빈 상태(표·셀렉트 목록) — 간단 아이콘 + '데이터 없음'. 컨덕터 데모와 같은 그림.
 * 화면 안의 ConfigProvider(테마)는 이 값을 물려받는다(antd 는 자식 Provider 가 안 준 설정을 부모에서 잇는다).
 * 손코드 화면(mwms-ds)엔 antd 컴포넌트가 없어 영향이 없다.
 */
const renderPboEmpty = () => <core.Empty image={core.Empty.PRESENTED_IMAGE_SIMPLE} description="데이터 없음" />

function MainLayout() {
  const { isModalOpen } = useProgressModal()

  return (
    <MenuOnSessionProvider>
      <LayoutWrapper>
        <HeaderWrapper $disabled={isModalOpen}>
          <MainHeader />
        </HeaderWrapper>
        <LayoutMid>
          <SideBarWrapper $disabled={isModalOpen}>
            <MainSideBar />
          </SideBarWrapper>
          <LayoutContentWrap>
            <MenuOnSessionWrapper $disabled={isModalOpen}>
              <MainMenuOnSession />
            </MenuOnSessionWrapper>
            <LayoutContent>
              <core.ConfigProvider renderEmpty={renderPboEmpty}>
                <MenuGuard>
                  <Outlet />
                </MenuGuard>
              </core.ConfigProvider>
            </LayoutContent>
          </LayoutContentWrap>
        </LayoutMid>
        <MainFooter />
      </LayoutWrapper>
    </MenuOnSessionProvider>
  )
}

const LayoutWrapper = styled.div`
  background-image: url('${Utils.assetUrl('/login_bg_1.jpg')}');
  background-size: cover;
  background-repeat: no-repeat;
  width: 100%;
  height: 100%;
`

const HeaderWrapper = styled.div<{ $disabled: boolean }>`
  position: relative;
  ${({ $disabled }) =>
    $disabled &&
    `
    pointer-events: none;
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(0, 0, 0, 0.3);
      z-index: 1000;
    }
  `}
`

const SideBarWrapper = styled.div<{ $disabled: boolean }>`
  position: relative;
  ${({ $disabled }) =>
    $disabled &&
    `
    pointer-events: none;
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(0, 0, 0, 0.3);
      z-index: 1000;
    }
  `}
`

const MenuOnSessionWrapper = styled.div<{ $disabled: boolean }>`
  position: relative;
  ${({ $disabled }) =>
    $disabled &&
    `
    pointer-events: none;
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(0, 0, 0, 0.3);
      z-index: 1000;
    }
  `}
`

const LayoutMid = styled.div`
  display: flex;
  height: calc(100vh - ${HEADER_HEIGHT}px - ${FOOTER_HEIGHT}px);
`

const LayoutContentWrap = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  width: 100%;
  flex: 1 1 auto;
  overflow: hidden;
`

const LayoutContent = styled.div`
  height: 100%;
  padding: ${LAYOUT_CONTENT_PADDING}px;
  overflow-y: auto;
  background: #fff;
  /* 컨덕터 어댑터가 sticky 제목 행 offset 계산에 쓰는 CSS 변수. 값 = 이 스크롤 컨테이너의 padding. */
  --pbo-page-header-scroller-pad: ${LAYOUT_CONTENT_PADDING}px;
`

export default MainLayout
